import React, { useState } from "react";
import { Modal, Button, Form, Alert, Card, Row, Col, Badge, ProgressBar, Spinner, Tab, Tabs } from "react-bootstrap";
import { FaCheck, FaTimes, FaExclamationTriangle, FaFilter, FaList, FaChartBar } from "react-icons/fa";
import { bulkTransitionGradeLevel } from "../services/APIClient";
import "./BulkGradeTransitionModal.css";

const BulkGradeTransitionModal = ({ show, onHide, schoolId }) => {
  const [phase, setPhase] = useState("input"); // 'input', 'loading', 'results'
  const [formData, setFormData] = useState({
    currentGradeLevel: "1",
    newAcademicYear: "",
    transitionReason: "Automated Grade Promotion"
  });
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("summary");

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.currentGradeLevel) {
      setError("Please select a grade level");
      return false;
    }
    if (!formData.newAcademicYear) {
      setError("Please select an academic year");
      return false;
    }
    return true;
  };

  const handleExecuteTransition = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setPhase("loading");

    try {
      const result = await bulkTransitionGradeLevel(
        schoolId,
        parseInt(formData.currentGradeLevel),
        formData.newAcademicYear,
        formData.transitionReason
      );

      setResults(result);
      setPhase("results");
    } catch (err) {
      setError(`Failed to execute transition: ${err.message}`);
      setPhase("input");
    }
  };

  const handleClose = () => {
    setPhase("input");
    setFormData({
      currentGradeLevel: "1",
      newAcademicYear: "",
      transitionReason: "Automated Grade Promotion"
    });
    setResults(null);
    setError(null);
    onHide();
  };

  // Generate academic years (current year +/- 2 years)
  const generateAcademicYears = () => {
    const years = [];
    const currentYear = new Date().getFullYear();
    
    for (let i = 0; i <= 3; i++) {
      const startYear = currentYear + i;
      const endYear = startYear + 1;
      const startYY = startYear.toString().slice(-2);
      const endYY = endYear.toString().slice(-2);
      years.push(`${startYY}-${endYY}`);
    }
    
    return years;
  };

  // ============ PHASE 1: INPUT FORM ============
  const InputPhase = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Bulk Grade Transition</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-4">
          <FaExclamationTriangle className="me-2" />
          <strong>Important:</strong> This will transition all eligible classes from the selected grade level to the next grade level. Original classes will be marked as snapshots and cannot be edited. This action cannot be undone.
        </Alert>

        <Form>
          <Form.Group className="mb-3">
            <Form.Label><strong>Current Grade Level</strong></Form.Label>
            <Form.Select
              name="currentGradeLevel"
              value={formData.currentGradeLevel}
              onChange={handleInputChange}
            >
              <option value="">Select grade level to transition...</option>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(grade => (
                <option key={grade} value={grade}>
                  Grade {grade} → Grade {grade + 1}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              All Grade {formData.currentGradeLevel || "?"} classes will be transitioned to Grade {parseInt(formData.currentGradeLevel || 0) + 1}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label><strong>New Academic Year</strong></Form.Label>
            <Form.Select
              name="newAcademicYear"
              value={formData.newAcademicYear}
              onChange={handleInputChange}
            >
              <option value="">Select academic year...</option>
              {generateAcademicYears().map(year => (
                <option key={year} value={year}>
                  {year} ({`20${year.split('-')[0]}`}-{`20${year.split('-')[1]}`})
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Transition Reason <span className="text-muted">(Optional)</span></Form.Label>
            <Form.Control
              type="text"
              name="transitionReason"
              value={formData.transitionReason}
              onChange={handleInputChange}
            />
          </Form.Group>
        </Form>

        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleExecuteTransition}
          disabled={!formData.currentGradeLevel || !formData.newAcademicYear}
        >
          Execute Transition
        </Button>
      </Modal.Footer>
    </>
  );

  // ============ PHASE 2: LOADING ============
  const LoadingPhase = () => (
    <>
      <Modal.Header>
        <Modal.Title>Executing Grade Transition</Modal.Title>
      </Modal.Header>
      <Modal.Body className="text-center py-5">
        <Spinner animation="border" role="status" className="mb-3">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <h5>Transitioning classes...</h5>
        <p className="text-muted">
          Grade {formData.currentGradeLevel} → Grade {parseInt(formData.currentGradeLevel) + 1}<br />
          Academic Year: {formData.newAcademicYear}
        </p>
        <p className="text-muted small mt-3">This may take a few moments</p>
      </Modal.Body>
    </>
  );

  // ============ PHASE 3: RESULTS ============
  const ResultsPhase = () => {
    if (!results) return null;

    const {
      success,
      executionTimestamp,
      summary,
      filterSummary,
      results: classResults = [],
      errors: errorsList = []
    } = results;

    const successRate = summary.totalClasses > 0
      ? Math.round((summary.successfulTransitions / (summary.successfulTransitions + summary.failedTransitions)) * 100)
      : 0;

    return (
      <>
        <Modal.Header closeButton={false}>
          <Modal.Title>
            {success ? (
              <><FaCheck className="text-success me-2" />Transition Complete</>
            ) : (
              <><FaExclamationTriangle className="text-warning me-2" />Transition Completed with Errors</>
            )}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="results-body">
          {/* SUCCESS/ERROR BANNER */}
          {success ? (
            <Alert variant="success" className="mb-4">
              <FaCheck className="me-2" />
              <strong>Success!</strong> All classes have been transitioned successfully.
            </Alert>
          ) : (
            <Alert variant="warning" className="mb-4">
              <FaExclamationTriangle className="me-2" />
              <strong>Completed with Issues:</strong> Some classes failed to transition. Check the errors tab for details.
            </Alert>
          )}

          {/* EXECUTION INFO */}
          <div className="mb-4 text-muted small">
            <div>Executed: {new Date(executionTimestamp).toLocaleString()}</div>
            <div>Grade Transition: {formData.currentGradeLevel} → {parseInt(formData.currentGradeLevel) + 1}</div>
            <div>Academic Year: {formData.newAcademicYear}</div>
          </div>

          {/* TABBED RESULTS */}
          <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
            
            {/* SUMMARY TAB */}
            <Tab eventKey="summary" title={<><FaChartBar className="me-2" />Summary</>}>
              <div className="results-summary mt-4">
                <Row className="mb-4">
                  <Col md={6}>
                    <Card className="stat-card success-card">
                      <Card.Body>
                        <div className="stat-label">Successful Transitions</div>
                        <div className="stat-value">{summary.successfulTransitions}</div>
                        <div className="stat-subtext">{successRate}% success rate</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className={`stat-card ${summary.failedTransitions > 0 ? 'danger-card' : 'success-card'}`}>
                      <Card.Body>
                        <div className="stat-label">Failed Transitions</div>
                        <div className="stat-value">{summary.failedTransitions}</div>
                        <div className="stat-subtext">Classes that encountered errors</div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Row className="mb-4">
                  <Col md={6}>
                    <Card className="stat-card">
                      <Card.Body>
                        <div className="stat-label">Students Affected</div>
                        <div className="stat-value">{summary.totalStudentsAffected}</div>
                        <div className="stat-subtext">Total students transitioned to new classes</div>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="stat-card">
                      <Card.Body>
                        <div className="stat-label">Total Classes Found</div>
                        <div className="stat-value">{summary.totalClasses}</div>
                        <div className="stat-subtext">
                          {summary.eligibleClasses} eligible, {summary.totalClasses - summary.eligibleClasses} filtered
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Card className="progress-card">
                  <Card.Body>
                    <div className="mb-3">
                      <strong>Overall Progress</strong>
                      <ProgressBar
                        now={successRate}
                        variant={successRate === 100 ? "success" : successRate >= 80 ? "info" : "warning"}
                        className="mt-2"
                      />
                    </div>
                    <div className="text-muted small">
                      {summary.successfulTransitions} of {summary.successfulTransitions + summary.failedTransitions} classes successfully transitioned
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Tab>

            {/* FILTERS TAB */}
            <Tab eventKey="filters" title={<><FaFilter className="me-2" />Filters Applied</>}>
              <div className="results-filters mt-4">
                <p className="text-muted mb-4">
                  <strong>{filterSummary.filtered}</strong> classes were excluded from the transition:
                </p>

                <div className="filter-breakdown">
                  {filterSummary.filterReasons.fakeOrTrialName > 0 && (
                    <div className="filter-item">
                      <Badge bg="warning" className="me-2">
                        {filterSummary.filterReasons.fakeOrTrialName}
                      </Badge>
                      Classes with "Fake" or "Trial" in the name
                    </div>
                  )}
                  {filterSummary.filterReasons.zeroStudents > 0 && (
                    <div className="filter-item">
                      <Badge bg="warning" className="me-2">
                        {filterSummary.filterReasons.zeroStudents}
                      </Badge>
                      Empty classes (0 students)
                    </div>
                  )}
                  {filterSummary.filterReasons.alreadySnapshot > 0 && (
                    <div className="filter-item">
                      <Badge bg="warning" className="me-2">
                        {filterSummary.filterReasons.alreadySnapshot}
                      </Badge>
                      Already snapshot classes
                    </div>
                  )}
                  {filterSummary.filterReasons.trialClass > 0 && (
                    <div className="filter-item">
                      <Badge bg="warning" className="me-2">
                        {filterSummary.filterReasons.trialClass}
                      </Badge>
                      Trial classes
                    </div>
                  )}
                  {filterSummary.filterReasons.specialEdClass > 0 && (
                    <div className="filter-item">
                      <Badge bg="warning" className="me-2">
                        {filterSummary.filterReasons.specialEdClass}
                      </Badge>
                      Special education classes
                    </div>
                  )}
                  {filterSummary.filterReasons.setClass > 0 && (
                    <div className="filter-item">
                      <Badge bg="warning" className="me-2">
                        {filterSummary.filterReasons.setClass}
                      </Badge>
                      Set classes
                    </div>
                  )}
                  {filterSummary.filterReasons.deleted > 0 && (
                    <div className="filter-item">
                      <Badge bg="warning" className="me-2">
                        {filterSummary.filterReasons.deleted}
                      </Badge>
                      Deleted classes
                    </div>
                  )}
                </div>

                <Card className="mt-4 bg-light">
                  <Card.Body>
                    <strong>Why classes are filtered:</strong>
                    <ul className="mt-2 mb-0 small">
                      <li>Test/Trial classes should not disrupt real student records</li>
                      <li>Empty classes have no students to transition</li>
                      <li>Special education & set classes have unique configurations</li>
                      <li>Already-snapshot classes are historical records</li>
                    </ul>
                  </Card.Body>
                </Card>
              </div>
            </Tab>

            {/* CLASS RESULTS TAB */}
            <Tab eventKey="classes" title={<><FaList className="me-2" />Classes ({classResults.length})</>}>
              <div className="results-classes mt-4">
                {classResults.length === 0 ? (
                  <p className="text-muted">No classes were processed.</p>
                ) : (
                  <div className="class-results-list">
                    {classResults.map((classResult, index) => (
                      <ClassResultCard key={index} classResult={classResult} />
                    ))}
                  </div>
                )}
              </div>
            </Tab>

            {/* ERRORS TAB */}
            {errorsList.length > 0 && (
              <Tab eventKey="errors" title={<><FaTimes className="me-2" />Errors ({errorsList.length})</>}>
                <div className="results-errors mt-4">
                  {errorsList.map((error, index) => (
                    <Alert key={index} variant="danger" className="mb-3">
                      <strong>{error.className || error.classId}</strong>
                      <div className="mt-2 text-danger-darker">{error.error}</div>
                    </Alert>
                  ))}
                </div>
              </Tab>
            )}

          </Tabs>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </>
    );
  };

  // ============ CLASS RESULT CARD ============
  const ClassResultCard = ({ classResult }) => {
    const { originalClassId, className, result } = classResult;
    const isSuccess = result.success;

    return (
      <Card className={`mb-3 class-result-card ${isSuccess ? 'success' : 'failed'}`}>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <h6 className="mb-1">
                {isSuccess ? (
                  <FaCheck className="text-success me-2" />
                ) : (
                  <FaTimes className="text-danger me-2" />
                )}
                {className}
              </h6>
              <div className="text-muted small">{originalClassId}</div>
            </div>
            <Badge bg={isSuccess ? "success" : "danger"}>
              {isSuccess ? "Success" : "Failed"}
            </Badge>
          </div>

          {isSuccess ? (
            <div className="mt-3 success-details">
              <div className="detail-row">
                <span className="label">Original Class ID:</span>
                <span className="value">{result.snapshotClassId}</span>
              </div>
              <div className="detail-row">
                <span className="label">New Class ID:</span>
                <span className="value">{result.newClassId}</span>
              </div>
              <div className="detail-row">
                <span className="label">Students Transitioned:</span>
                <span className="value"><strong>{result.studentsUpdated}</strong></span>
              </div>
              {result.studentsBeforeTransition !== undefined && (
                <div className="detail-row">
                  <span className="label">Students Before:</span>
                  <span className="value">{result.studentsBeforeTransition}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-3 failed-details">
              <Alert variant="danger" className="mb-0">
                {result.error || "Unknown error"}
              </Alert>
            </div>
          )}
        </Card.Body>
      </Card>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      {phase === "input" && <InputPhase />}
      {phase === "loading" && <LoadingPhase />}
      {phase === "results" && <ResultsPhase />}
    </Modal>
  );
};

export default BulkGradeTransitionModal;